<template>
  <b-modal
    v-model="showModal"
    title="Importar desde Excel"
    @hidden="onClose"
    hide-footer
    size="lg"
    :no-close-on-backdrop="sendingPallets"
    :no-close-on-esc="sendingPallets"
  >
    <b-card-body>
      <b-form @submit.prevent="handleAction">
        <b-form-group
          label="Seleccione un archivo Excel"
          description="Seleccione un archivo .xlsx o .xls para procesar"
          v-if="!processed"
        >
          <b-form-file
            v-model="file"
            accept=".xlsx, .xls"
            placeholder="Seleccione un archivo..."
            browse-text="Buscar"
            @change="onFileChange"
            :disabled="sendingPallets"
          ></b-form-file>
        </b-form-group>

        <!-- Progress durante el envío -->
        <div v-if="sendingPallets" class="mb-3">
          <h5>Enviando pallets...</h5>
          <b-progress :max="totalPallets" height="2rem" class="mb-2">
            <b-progress-bar
              :value="processedPalletsCount"
              :label="`${processedPalletsCount} / ${totalPallets}`"
              variant="primary"
            ></b-progress-bar>
          </b-progress>
          <p class="text-center">Por favor espere, no cierre esta ventana...</p>
        </div>

        <!-- Resultados después de procesar el Excel -->
        <div v-if="processed && !sendingPallets && !finalResults">
          <b-alert variant="info" show>
            <strong>{{ extractedNumbers.length }} pallets encontrados</strong>
            <div class="mt-2" style="max-height: 200px; overflow-y: auto">
              <b-badge
                v-for="num in extractedNumbers"
                :key="num"
                variant="info"
                class="mr-1 mb-1"
              >
                {{ num }}
              </b-badge>
            </div>
          </b-alert>
        </div>

        <!-- Resultados finales -->
        <div v-if="finalResults">
          <b-alert variant="success" show v-if="successfulPallets.length > 0">
            <strong
              >✓ {{ successfulPallets.length }} pallets agregados
              correctamente</strong
            >
            <div class="mt-2" style="max-height: 150px; overflow-y: auto">
              <small>
                <b-badge
                  v-for="pallet in successfulPallets"
                  :key="pallet.number"
                  variant="success"
                  class="mr-1 mb-1"
                >
                  {{ pallet.number }}
                </b-badge>
              </small>
            </div>
          </b-alert>

          <b-alert variant="danger" show v-if="failedPallets.length > 0">
            <strong>✗ {{ failedPallets.length }} pallets con errores</strong>
            <div class="mt-2" style="max-height: 150px; overflow-y: auto">
              <small>
                <div
                  v-for="pallet in failedPallets"
                  :key="pallet.number"
                  class="mb-1"
                >
                  <b-badge variant="danger">{{ pallet.number }}</b-badge>
                  - {{ pallet.error }}
                </div>
              </small>
            </div>
          </b-alert>
        </div>

        <b-alert v-if="errorMessage" variant="danger" show>
          {{ errorMessage }}
        </b-alert>

        <div class="mt-3 text-right">
          <b-button
            variant="secondary"
            @click="onClose"
            class="mr-2"
            :disabled="sendingPallets"
          >
            {{ finalResults ? "Cerrar" : "Cancelar" }}
          </b-button>
          <b-button
            v-if="!finalResults"
            variant="primary"
            @click="handleAction"
            :disabled="(!file && !processed) || sendingPallets"
          >
            <b-spinner small v-if="processing || sendingPallets"></b-spinner>
            {{
              sendingPallets
                ? "Enviando..."
                : processing
                ? "Procesando..."
                : processed
                ? "Confirmar"
                : "Procesar"
            }}
          </b-button>
        </div>
      </b-form>
    </b-card-body>
  </b-modal>
</template>

<script>
import * as XLSX from "xlsx";
import miniToastr from "mini-toastr";

export default {
  name: "ImportExcelModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    waybillId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      file: null,
      processing: false,
      sendingPallets: false,
      processed: false,
      finalResults: false,
      errorMessage: "",
      successMessage: "",
      extractedNumbers: [],
      successfulPallets: [],
      failedPallets: [],
      processedPalletsCount: 0,
      totalPallets: 0
    };
  },
  computed: {
    showModal: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.onClose();
        }
      }
    }
  },
  methods: {
    onFileChange() {
      this.errorMessage = "";
      this.successMessage = "";
      this.processed = false;
      this.finalResults = false;
    },
    onClose() {
      this.file = null;
      this.errorMessage = "";
      this.successMessage = "";
      this.processing = false;
      this.sendingPallets = false;
      this.processed = false;
      this.finalResults = false;
      this.extractedNumbers = [];
      this.successfulPallets = [];
      this.failedPallets = [];
      this.processedPalletsCount = 0;
      this.totalPallets = 0;
      this.$emit("close");
    },
    handleAction() {
      if (!this.processed) {
        this.processExcel();
      } else {
        this.confirmAndSendPallets();
      }
    },
    async processExcel() {
      if (!this.file) {
        this.errorMessage = "Por favor seleccione un archivo";
        return;
      }

      this.processing = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const data = await this.readExcelFile(this.file);
        const uniqueNumbers = this.extractUniqueNumbersFromColumnA(data);

        if (uniqueNumbers.length === 0) {
          this.errorMessage =
            "No se encontraron números en la columna A del archivo Excel.";
          return;
        }

        // Console log de los números distintos encontrados
        console.log(
          "Números distintos encontrados en la columna A:",
          uniqueNumbers
        );

        this.extractedNumbers = uniqueNumbers;
        this.processed = true;
      } catch (error) {
        console.error("Error al procesar el archivo:", error);
        this.errorMessage =
          "Error al procesar el archivo. Asegúrese de que sea un archivo Excel válido.";
      } finally {
        this.processing = false;
      }
    },
    async confirmAndSendPallets() {
      this.sendingPallets = true;
      this.successfulPallets = [];
      this.failedPallets = [];
      this.processedPalletsCount = 0;
      this.totalPallets = this.extractedNumbers.length;
      this.errorMessage = "";

      for (const palletNumber of this.extractedNumbers) {
        try {
          await this.sendPalletToDispatch(palletNumber);
          // Pequeño delay entre requests
          await this.delay(400);
        } catch (error) {
          console.error(`Error enviando pallet ${palletNumber}:`, error);
        }
        this.processedPalletsCount++;
      }

      this.sendingPallets = false;
      this.finalResults = true;

      // Emitir evento para refrescar la tabla en el componente padre
      this.$emit("palletsAdded");

      // Mostrar notificación final
      if (this.successfulPallets.length > 0) {
        miniToastr.success(
          `${this.successfulPallets.length} pallets agregados correctamente`,
          ""
        );
      }
      if (this.failedPallets.length > 0) {
        miniToastr.warn(
          `${this.failedPallets.length} pallets no pudieron ser agregados`,
          ""
        );
      }
    },
    async sendPalletToDispatch(palletNumber) {
      const data = {
        numero_pallet: palletNumber,
        waybillId: this.waybillId
      };

      try {
        const response = await this.$axios.put(
          "/api/v1/allstock/dispatch_pallet/",
          data
        );

        // Convertir status a string para comparación (el backend retorna string)
        const status = String(response.data.status);
        
        // Status 1: Éxito
        if (status === "1") {
          this.successfulPallets.push({
            number: palletNumber,
            message: response.data.message
          });
        } 
        // Status 0: Pallet no existe
        // Status 2: Ya fue despachado
        // Status 3: Pertenece a otro cliente
        else if (status === "0" || status === "2" || status === "3") {
          this.failedPallets.push({
            number: palletNumber,
            error: response.data.message || "Error desconocido"
          });
        }
        // Cualquier otro status
        else {
          this.failedPallets.push({
            number: palletNumber,
            error: response.data.message || "Error desconocido"
          });
        }
      } catch (error) {
        // Error de red o del servidor
        let errorMessage = "Error al conectar con el servidor";
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.failedPallets.push({
          number: palletNumber,
          error: errorMessage
        });
      }
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            // Obtener la primera hoja
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convertir a JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = (error) => {
          reject(error);
        };

        reader.readAsArrayBuffer(file);
      });
    },
    extractUniqueNumbersFromColumnA(data) {
      const pallets = new Set();

      // Iterar sobre todas las filas, saltando la primera (headers)
      data.forEach((row, index) => {
        // Saltar la primera fila (índice 0)
        if (index === 0) {
          return;
        }

        if (row && row.length > 0) {
          const cellValue = row[0]; // Columna A (índice 0)

          if (
            cellValue !== null &&
            cellValue !== undefined &&
            cellValue !== ""
          ) {
            // Convertir el valor a string y limpiar espacios
            const palletValue = String(cellValue).trim();

            // Agregar el valor como string (puede contener letras y números)
            if (palletValue.length > 0) {
              pallets.add(palletValue);
            }
          }
        }
      });

      // Convertir Set a Array y ordenar alfabéticamente
      return Array.from(pallets).sort();
    }
  }
};
</script>

<style scoped>
.text-right {
  text-align: right;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}
</style>
